import { ChangeEvent, FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';

import { Grid, IconButton, Input, Paper, Stack } from '@mui/material';
import {
  Cancel as CancelIcon,
  Done as SaveIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CopyAll,
  Add as CreateIcon,
} from '@mui/icons-material';

import { Table } from '../../components/table/table';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import {
  PutMerchantDTO,
  useGetMerchantCoinConfigQuery,
  usePutMerchantCoinConfigGoupsMutation,
} from '../../api/controllers/merchant';
import { Merchant } from '../../types/models/merchant.model';
import { permissionsCheck } from '../../utils/permission';
import { formatAmount } from '../../utils';

export const MerchantСoinСonfig: FC = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const hasPermissionToEditMerchantСoinСonfig = permissionsCheck(['WRITE_MERCHANT_COIN_CONFIGS']);

  // api data
  const {
    data: merchantCoinConfigs,
    isLoading: isMerchantLoading,
    refetch,
  } = useGetMerchantCoinConfigQuery();
  const [updateMerchant, { isLoading: isUpdateMerchantLoading }] =
    usePutMerchantCoinConfigGoupsMutation();

  // edit fields logic
  const [editFields, setEditFields] = useState<Merchant | null>(null);

  const handleEditFields = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const name = e.target.name;
    const value = e.target.value;

    // @ts-ignore
    setEditFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };

  // edit mode logic
  const [activeRowId, setActiveRowId] = useState<number | null>();
  const [isEditMode, setEditMode] = useState(false);
  const startEditMode = (merchant: Merchant) => {
    setEditFields(merchant);
    setActiveRowId(merchant.id);
    setEditMode(true);
  };
  const offEditMode = () => {
    setEditFields(null);
    setActiveRowId(null);
    setEditMode(false);
  };

  // delete fee handler

  // edit merchant markup logic
  const editMerchantMarkup = async () => {
    if (!editFields || !editFields.ttl_seconds || !activeRowId) return;

    const body: PutMerchantDTO['body'] = {
      ttl_seconds: Number(editFields.ttl_seconds),
      min_amount: String(editFields.min_amount),
    };

    try {
      await updateMerchant({ merchant_coin_config_id: activeRowId, body }).unwrap();
      enqueueSnackbar(t('ui.snackbar.success'), { variant: 'success' });
      offEditMode();
      refetch();
    } catch (e) {
      // @ts-ignore
      enqueueSnackbar(e?.data?.message ?? t('ui.snackbar.error'), { variant: 'error' });
    }
  };

  // table cells and rows
  const headCells: HeadCell[] = [
    { id: '1', label: t('merchant-coin-configs.head-cells.id') },
    { id: '2', label: t('merchant-coin-configs.head-cells.coin-name') },
    { id: '3', label: t('merchant-coin-configs.head-cells.ttl-seconds') },
    { id: '4', label: t('merchant-coin-configs.head-cells.min-amount') },
    {
      id: '5',
      label: t('merchant-coin-configs.head-cells.actions'),
      hidden: !hasPermissionToEditMerchantСoinСonfig,
    },
  ];

  const rows: Row[] | undefined = merchantCoinConfigs?.configs?.map((it) => {
    const {
      id,
      coin: { name },
      ttl_seconds,
      min_amount,
    } = it;

    const cells: RowCell[] = [
      { name: 'id', value: id },
      {
        name: 'coin_name',
        value: name,
      },
      {
        name: 'ttl_seconds',
        value: ttl_seconds,
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="ttl_seconds"
              type="number"
              value={editFields?.ttl_seconds ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isUpdateMerchantLoading}
            />
          ) : undefined,
      },
      {
        name: 'min_amount',
        value: formatAmount(min_amount),
        component:
          isEditMode && activeRowId === id ? (
            <Input
              name="min_amount"
              type="number"
              value={editFields?.min_amount ?? ''}
              onChange={(e) => handleEditFields(e)}
              disabled={isUpdateMerchantLoading}
            />
          ) : undefined,
      },
      {
        name: 'actions',
        hidden: !hasPermissionToEditMerchantСoinСonfig,
        component: isEditMode ? (
          activeRowId === id ? (
            <Stack direction="row" spacing={2}>
              <IconButton
                aria-label="save"
                color="success"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  editMerchantMarkup();
                }}
                disabled={isUpdateMerchantLoading}
              >
                <SaveIcon />
              </IconButton>

              <IconButton
                aria-label="cancel"
                color="error"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  offEditMode();
                }}
                disabled={isUpdateMerchantLoading}
              >
                <CancelIcon />
              </IconButton>
            </Stack>
          ) : undefined
        ) : (
          <Stack direction="row" spacing={2}>
            <IconButton
              aria-label="edit"
              color="warning"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                startEditMode(it);
              }}
              disabled={isMerchantLoading}
            >
              <EditIcon />
            </IconButton>
          </Stack>
        ),
      },
    ];

    return {
      cells,
      hover: false,
    };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} />
        </Paper>
      </Grid>
    </>
  );
};
